/**
 * Unified import landing: one dropzone that handles 1..N files.
 *
 * Routing:
 *   - 1 file picked → single-import flow (no batch row, navigate to
 *     `/contracts/imports/:id` where the tracker polls).
 *   - 2+ files picked → batch flow (open `import_batches` row, link N
 *     `imported_documents` rows to it, navigate to
 *     `/contracts/imports/bulk/:batchId`).
 *
 * The component is intentionally chunky and visual — the import surface
 * is one of the two main ways a user adds contracts (the other being
 * "+ Skapa avtal" from a template), so it earns the real estate.
 */

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tenantApi, type ImportBatchListRow, type ImportListRow } from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useTenantQuery } from '../query.js';
import { FileTypeIcon } from './FileTypeIcon.js';

const PDF_MIME = 'application/pdf';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const ACCEPT = `${PDF_MIME},${DOCX_MIME},.pdf,.docx`;
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 50;
const UPLOAD_CONCURRENCY = 4;

type PerFileState =
  | { kind: 'pending' }
  | { kind: 'uploading' }
  | { kind: 'registered' }
  | { kind: 'error'; message: string };

export function ImportLanding({ groupReady }: { groupReady: boolean }) {
  const { token } = useTenantSession();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [picked, setPicked] = useState<File[]>([]);
  const [fileStates, setFileStates] = useState<PerFileState[]>([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addFiles(files: FileList | File[]) {
    setError(null);
    const arr = Array.from(files);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const f of arr) {
      const mime = mimeOf(f);
      if (mime !== PDF_MIME && mime !== DOCX_MIME) {
        rejected.push(`${f.name} (otillåten filtyp)`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        rejected.push(`${f.name} (>25 MB)`);
        continue;
      }
      accepted.push(f);
    }
    const combined = [...picked, ...accepted].slice(0, MAX_FILES);
    setPicked(combined);
    if (rejected.length) {
      setError(`Hoppade över: ${rejected.join(', ')}`);
    }
  }

  function removePicked(idx: number) {
    setPicked((prev) => prev.filter((_, i) => i !== idx));
  }

  function clearPicked() {
    setPicked([]);
    setFileStates([]);
    setError(null);
  }

  async function onStart() {
    if (!token || !groupReady || picked.length === 0) return;
    setStarting(true);
    setError(null);

    // Single file → keep the existing single-import flow (no batch row).
    if (picked.length === 1) {
      const file = picked[0]!;
      const mime = mimeOf(file);
      try {
        const presigned = await tenantApi.imports.upload_url({
          filename: file.name,
          mime,
          bytes: file.size,
        });
        await putToStorage(presigned.upload_url, file, mime);
        await tenantApi.imports.create({
          document_id: presigned.document_id,
          raw_key: presigned.raw_key,
          filename: file.name,
          mime,
          bytes: file.size,
        });
        navigate(`/contracts/imports/${presigned.document_id}`);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setStarting(false);
      }
      return;
    }

    // 2+ files → batch flow.
    try {
      const batch = await tenantApi.imports.batch_start({
        total_count: picked.length,
      });
      setFileStates(picked.map(() => ({ kind: 'pending' as const })));

      // Concurrency cap so we don't try to PUT 50 files simultaneously.
      const queue = picked.map((f, i) => ({ file: f, idx: i }));
      let cursor = 0;
      let successCount = 0;
      const workers: Promise<void>[] = [];
      const worker = async () => {
        while (cursor < queue.length) {
          const job = queue[cursor++];
          if (!job) break;
          const ok = await uploadOne(job.file, job.idx, batch.batch_id);
          if (ok) successCount++;
        }
      };
      for (let w = 0; w < Math.min(UPLOAD_CONCURRENCY, queue.length); w++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      // If every file failed before reaching `imports.create`, the batch
      // is empty and the bulk-detail view would just show a confusing
      // "0 av N filer registrerade" with no per-file reason. Keep the
      // user here so they can read the error badges on each tile.
      if (successCount === 0) {
        setError(
          `Ingen fil kunde laddas upp (0 av ${picked.length}). Se felmeddelandet på respektive fil och försök igen.`,
        );
        return;
      }

      navigate(`/contracts/imports/bulk/${batch.batch_id}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  }

  async function uploadOne(file: File, idx: number, batchId: string): Promise<boolean> {
    if (!token) return false;
    setFileStates((prev) => withAt(prev, idx, { kind: 'uploading' }));
    try {
      const mime = mimeOf(file);
      const presigned = await tenantApi.imports.upload_url({
        filename: file.name,
        mime,
        bytes: file.size,
        batch_id: batchId,
      });
      await putToStorage(presigned.upload_url, file, mime);
      await tenantApi.imports.create({
        document_id: presigned.document_id,
        raw_key: presigned.raw_key,
        filename: file.name,
        mime,
        bytes: file.size,
        batch_id: batchId,
      });
      setFileStates((prev) => withAt(prev, idx, { kind: 'registered' }));
      return true;
    } catch (e) {
      setFileStates((prev) =>
        withAt(prev, idx, { kind: 'error', message: (e as Error).message }),
      );
      return false;
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {picked.length === 0 ? (
        <BigDropzone
          fileInputRef={fileInputRef}
          onPick={addFiles}
          disabled={!groupReady || starting}
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-2">
            <div className="text-sm">
              <span className="font-medium text-stone-800">{picked.length}</span>
              <span className="text-stone-600">
                {' '}fil{picked.length === 1 ? '' : 'er'} klara att importera
              </span>
              {picked.length >= 2 && (
                <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
                  Bulkimport
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={starting || picked.length >= MAX_FILES}
                className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs hover:bg-stone-50 disabled:opacity-50"
              >
                + Lägg till fler
              </button>
              <button
                type="button"
                onClick={clearPicked}
                disabled={starting}
                className="text-xs text-stone-500 hover:text-stone-800 disabled:opacity-50"
              >
                Rensa
              </button>
              <button
                type="button"
                onClick={onStart}
                disabled={!groupReady || starting}
                className="rounded bg-stone-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-stone-800 disabled:bg-stone-400"
              >
                {starting
                  ? 'Startar…'
                  : picked.length === 1
                    ? 'Starta import'
                    : `Starta bulkimport (${picked.length})`}
              </button>
            </div>
          </div>

          <FileTileGrid
            files={picked}
            states={fileStates}
            onRemove={starting ? undefined : removePicked}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}

      <RecentImports />
    </div>
  );
}

function BigDropzone({
  fileInputRef,
  onPick,
  disabled,
}: {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (files: FileList) => void;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      className={`relative flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed py-14 transition ${
        dragging
          ? 'border-stone-700 bg-stone-100'
          : 'border-stone-300 bg-gradient-to-b from-white to-stone-50'
      } ${disabled ? 'opacity-50' : 'hover:border-stone-400'}`}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) onPick(e.dataTransfer.files);
      }}
    >
      <FileStackIllustration />
      <div className="space-y-1 text-center">
        <div className="text-base font-medium text-stone-800">
          Dra och släpp avtal här
        </div>
        <div className="text-sm text-stone-500">
          Släpp <span className="font-medium">en eller flera</span> PDF/DOCX för att
          importera samtidigt
        </div>
      </div>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="rounded bg-stone-900 px-5 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-50"
      >
        Välj filer
      </button>
      <div className="text-[11px] text-stone-400">
        Max {MAX_FILES} filer per omgång · max 25 MB per fil · PDF eller DOCX
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          if (e.target.files) onPick(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function FileStackIllustration() {
  // Three stacked rectangles suggesting a pile of documents — pure
  // CSS, no asset dependency.
  return (
    <div className="relative h-16 w-16">
      <div className="absolute left-1 top-3 h-12 w-10 rotate-[-8deg] rounded-sm border border-stone-200 bg-white shadow-sm" />
      <div className="absolute left-3 top-2 h-12 w-10 rotate-[2deg] rounded-sm border border-stone-200 bg-white shadow-sm" />
      <div className="absolute left-5 top-1 h-12 w-10 rotate-[8deg] rounded-sm border border-stone-300 bg-white shadow">
        <div className="mx-1.5 mt-2 h-0.5 rounded bg-stone-300" />
        <div className="mx-1.5 mt-1 h-0.5 rounded bg-stone-300" />
        <div className="mx-1.5 mt-1 h-0.5 w-5 rounded bg-stone-300" />
      </div>
    </div>
  );
}

function FileTileGrid({
  files,
  states,
  onRemove,
}: {
  files: File[];
  states: PerFileState[];
  onRemove?: (idx: number) => void;
}) {
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {files.map((f, i) => {
        const state = states[i];
        return (
          <li
            key={`${f.name}-${i}`}
            className="group flex items-center gap-3 rounded border border-stone-200 bg-white p-3 text-sm shadow-sm"
          >
            <FileTypeIcon mime={mimeOf(f)} size="md" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-stone-800">{f.name}</div>
              <div className="text-xs text-stone-500">{formatBytes(f.size)}</div>
            </div>
            {state ? (
              <PerFileStateBadge state={state} />
            ) : onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label="Ta bort"
                className="text-stone-400 opacity-0 transition group-hover:opacity-100 hover:text-red-600"
              >
                ×
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function PerFileStateBadge({ state }: { state: PerFileState }) {
  if (state.kind === 'pending') return <Badge tone="muted">Köad</Badge>;
  if (state.kind === 'uploading') return <Badge tone="info">Laddar upp…</Badge>;
  if (state.kind === 'registered') return <Badge tone="ok">✓</Badge>;
  return (
    <Badge tone="error" title={state.message}>
      ✕
    </Badge>
  );
}

function Badge({
  children,
  tone,
  title,
}: {
  children: React.ReactNode;
  tone: 'ok' | 'info' | 'warn' | 'error' | 'muted';
  title?: string;
}) {
  const cls =
    tone === 'ok'
      ? 'bg-emerald-100 text-emerald-800'
      : tone === 'info'
        ? 'bg-sky-100 text-sky-800'
        : tone === 'warn'
          ? 'bg-amber-100 text-amber-900'
          : tone === 'error'
            ? 'bg-red-100 text-red-800'
            : 'bg-stone-100 text-stone-600';
  return (
    <span
      title={title}
      className={`inline-flex h-6 min-w-6 items-center justify-center rounded px-1.5 text-[11px] font-medium ${cls}`}
    >
      {children}
    </span>
  );
}

/**
 * Recent imports section: shows both bulk batches and single imports as
 * visual cards. We deliberately don't merge them — batches deserve the
 * larger summary card, single imports get a compact row.
 */
function RecentImports() {
  const batchesQ = useTenantQuery(
    'imports.batch_list',
    () => tenantApi.imports.batch_list(),
  );
  const singlesQ = useTenantQuery(
    'imports.list',
    () => tenantApi.imports.list(),
  );

  const batches = (batchesQ.data?.batches ?? []).slice(0, 6);
  // Hide single imports that belong to a batch — they're already
  // surfaced inside their batch card.
  const singles = (singlesQ.data?.imports ?? [])
    .filter((i) => !i.batchId)
    .slice(0, 6);

  if (batches.length === 0 && singles.length === 0) return null;

  return (
    <div className="space-y-4">
      {batches.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-stone-500">
            Senaste bulkimporter
          </h3>
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {batches.map((b) => (
              <li key={b.id}>
                <BatchCard batch={b} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {singles.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-stone-500">
            Senaste enstaka importer
          </h3>
          <ul className="divide-y divide-stone-100 rounded border border-stone-200 bg-white">
            {singles.map((s) => (
              <SingleImportRow key={s.id} item={s} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function BatchCard({ batch }: { batch: ImportBatchListRow }) {
  const c = batch.counts;
  const total = batch.totalCount;
  const done = c.extracted + c.confirmed + c.failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const allDone = done >= total;

  return (
    <Link
      to={`/contracts/imports/bulk/${batch.id}`}
      className="block rounded border border-stone-200 bg-white p-3 transition hover:border-stone-400 hover:shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-stone-800">
              {batch.label ?? `${total} avtal`}
            </span>
            <span className="text-[11px] text-stone-500">
              {formatAge(batch.createdAt)}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {c.extracted > 0 && (
              <span className="text-emerald-700">{c.extracted} klara</span>
            )}
            {c.confirmed > 0 && (
              <span className="text-stone-600">{c.confirmed} skapade</span>
            )}
            {c.failed > 0 && <span className="text-red-700">{c.failed} fel</span>}
            {c.in_progress > 0 && (
              <span className="text-stone-500">{c.in_progress} pågår</span>
            )}
          </div>
        </div>
        <div className="text-xs font-medium text-stone-500">
          {done}/{total}
        </div>
      </div>
      <div className="mt-2 flex h-1.5 w-full overflow-hidden rounded bg-stone-100">
        <div
          className={`h-full transition-all ${allDone ? 'bg-emerald-500' : 'bg-sky-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Link>
  );
}

function SingleImportRow({ item }: { item: ImportListRow }) {
  const href =
    item.contractId && item.status === 'confirmed'
      ? `/contracts/${item.contractId}/edit`
      : `/contracts/imports/${item.id}`;
  return (
    <li>
      <Link to={href} className="flex items-center gap-3 p-2.5 hover:bg-stone-50">
        <FileTypeIcon mime={null} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-stone-800">
            {item.rawFilename}
          </div>
          <div className="text-[11px] text-stone-500">{formatAge(item.createdAt)}</div>
        </div>
        <StatusBadge status={item.status} />
      </Link>
    </li>
  );
}

function StatusBadge({ status }: { status: ImportListRow['status'] }) {
  const map: Record<ImportListRow['status'], { tone: 'ok' | 'info' | 'warn' | 'error' | 'muted'; label: string }> = {
    uploaded: { tone: 'info', label: 'Uppladdad' },
    queued: { tone: 'info', label: 'I kö' },
    extracting: { tone: 'info', label: 'Läser…' },
    extracted: { tone: 'ok', label: 'Klar att granska' },
    failed_ocr_required: { tone: 'error', label: 'Skannad bild' },
    failed_extract: { tone: 'error', label: 'Fel' },
    confirmed: { tone: 'muted', label: 'Skapat' },
  };
  const m = map[status];
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

/**
 * PUT the file to the presigned storage URL with readable error
 * classification. A bare `fetch` failure (CORS preflight rejected,
 * DNS, offline) surfaces as `TypeError: Failed to fetch` — useless
 * to the user. Translate it into something they can act on.
 */
async function putToStorage(url: string, file: File, mime: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: { 'content-type': mime },
      body: file,
    });
  } catch {
    // No HTTP status reached — network down, CORS preflight blocked,
    // or DNS failure. Most likely culprit in dev: bucket CORS allowlist.
    throw new Error(
      'Kunde inte nå uppladdningstjänsten (nätverks- eller CORS-fel). Försök igen, eller kontakta support om problemet kvarstår.',
    );
  }
  if (!res.ok) {
    throw new Error(`Uppladdning avvisades av lagringen (HTTP ${res.status}).`);
  }
}

function mimeOf(f: File): string {
  if (f.type === PDF_MIME || f.type === DOCX_MIME) return f.type;
  const lower = f.name.toLowerCase();
  if (lower.endsWith('.pdf')) return PDF_MIME;
  if (lower.endsWith('.docx')) return DOCX_MIME;
  return f.type || '';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatAge(iso: string): string {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just nu';
    if (m < 60) return `${m} min sedan`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h sedan`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days} d sedan`;
    return d.toLocaleDateString('sv-SE');
  } catch {
    return iso;
  }
}

function withAt<T>(arr: T[], i: number, v: T): T[] {
  return arr.map((x, j) => (j === i ? v : x));
}
